import React, {useEffect, useRef} from 'react'
import styled from '@emotion/styled'
import {AnimatePresence, motion} from 'motion/react'

import {useRegisterBlockingSurface} from '../../../context/blockingSurfaces'
import {cssTheme} from '../../../themes'
import {closeOnEscape} from '../../../utils/closeOnEscape'
import transitions from '../../../utils/framerTransitions'
import {Button} from '../Button'
import {BottomBar} from '../helpers/BottomBar'
import {Portal} from '../helpers/Portal'
import {Icon} from '../Icon'
import {
  EndContent,
  NavBar,
  NavBarElement,
  NavBarProps,
  NavBarTitle
} from '../NavBar'
import {ANIMATION_TYPES} from '../Overlay'
import useBodyScrollLock from '../Overlay/useBodyScrollLock'

interface Props {
  /** The body of the pop-up */
  children: React.ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The callback for the close button click */
  closeAction?: () => void
  /** The data-id attribute for testing */
  dataId?: string
  /** The footer of the pop-up. Commonly used for action button placement */
  footer?: React.ReactNode
  /** Whether the BodyWrapper should have paddings around it */
  noPaddings?: boolean
  /** The height of the pop-up. This will be limited by the window height. */
  height?: number
  /** Whether the pop-up is open or not */
  isOpen: boolean
  /** The max-width of the pop-up. This will be limited by the default prop value. */
  width?: number
  /** Props forwarded to the NavBar rendered at the top of the pop-up. */
  navBarProps?: Omit<NavBarProps, 'isCompact' | 'bottomContent'>
  /** Whether the PopUp should call the onClose closeAction when the Esc key is pressed (true by default) */
  closeOnEsc?: boolean
  /** Whether the PopUp should call the onClose closeAction when the user clicks outside the PopUp (true by default) */
  closeOnOutsideClick?: boolean
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledPopUp = styled(PopUp)` */
  style?: React.CSSProperties
  /** Render children in a hidden div outside the portal so crawlers can index the content without JS */
  prerenderContent?: boolean
  /** Identifies this surface in the blocking-surface registry, so a component asking whether another surface is open can exclude its own */
  surfaceId?: string
}

export const TRANSITION_DURATION = 300 // ms

const Wrapper = styled(motion.div)`
  position: fixed;
  z-index: 101;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${cssTheme.colors.background.neutral.overlay};

  &.--is-chromatic {
    align-items: flex-start;
  }

  > div {
    padding: ${cssTheme.layout.spacing.s800};
  }
  ${NavBarElement} {
    &.--is-floating {
      pointer-events: none;
      z-index: 2;
      ${EndContent} {
        pointer-events: auto;
      }
    }
  }
`

export const Content = styled(motion.div)<{maxWidth: number; height?: number}>`
  height: ${({height}) => (height ? `${height}px` : 'auto')};
  max-height: ${({height}) =>
    height || `calc(100vh - ${cssTheme.layout.spacing.s950})`};
  max-width: ${({maxWidth}) => `${maxWidth}px`};
  margin: 0 ${cssTheme.layout.spacing.s300};
  border-radius: ${cssTheme.layout.radius.md};
  background: ${cssTheme.colors.background.neutral.primary};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`

export const HeaderWrapper = styled.div<{header?: React.ReactNode}>`
  position: relative;
`

const getPaddings = (
  hasHeader: boolean,
  noPaddings: boolean,
  footer: boolean
) => {
  if (noPaddings) {
    return '0'
  } else if (hasHeader && !footer) {
    return `0 ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s500}`
  } else if (hasHeader && footer) {
    return `0 ${cssTheme.layout.spacing.s500}`
  }
  return `${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500}`
}

export const BodyWrapper = styled.div<{
  hasHeader: boolean
  noPaddings: boolean
  footer: boolean
}>`
  padding: ${({hasHeader, noPaddings, footer}) =>
    getPaddings(hasHeader, noPaddings, footer)};

  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

export const Body = styled.div`
  flex: 0 1 auto;
  ${cssTheme.typography.bodyM};
`

export const Footer = styled.div`
  padding-top: ${cssTheme.layout.spacing.s500};
  ${cssTheme.typography.bodyM};
  text-align: center;
`

export const PopUp = ({
  children,
  className,
  closeAction,
  closeOnEsc = true,
  closeOnOutsideClick = true,
  dataId,
  footer,
  height,
  isOpen,
  navBarProps,
  noPaddings = false,
  width = 418,
  style,
  prerenderContent = false,
  surfaceId
}: Props) => {
  const popUpRef = useRef<HTMLDivElement>(null)
  const isCloseVisible = Boolean(closeAction)

  const {
    centerContent,
    endContent: navBarEndContent,
    withDivider,
    variant,
    floating,
    centerAligned = true,
    ...navBarRest
  } = navBarProps ?? {}

  const popUpHeight = () => {
    if (window.innerHeight > 1040 && height && height >= 960) {
      return 960
    } else if (height && height > window.innerHeight) {
      return window.innerHeight - 80
    }
    return height
  }

  const closeButton = (
    <Button
      variant={variant === 'inverse' ? 'ghostInverse' : 'ghost'}
      size="lg"
      hasTouchArea
      dataId="PopUpCloseButton"
      iconEnd={
        <Icon
          name="Close"
          size="lg"
          colorPath={
            variant === 'inverse'
              ? 'content.neutral.inverse'
              : 'content.neutral.primary'
          }
        />
      }
      onClick={closeAction}
    />
  )

  const headerEndContent =
    navBarEndContent ?? (isCloseVisible ? closeButton : undefined)

  // Only consumer-provided header *content* removes the body's top padding. A
  // variant/floating-only header (e.g. desktop LanguageCurrencySelector, which
  // floats just a close button over the body) reserves no space, so the body
  // keeps its top padding. The managed close button is excluded on purpose.
  const hasHeaderContent = Boolean(
    centerContent || navBarEndContent || navBarRest.startContent
  )

  useBodyScrollLock({ref: popUpRef, isEnabled: isOpen})
  useRegisterBlockingSurface(isOpen, surfaceId)

  useEffect(() => {
    if (closeAction && closeOnEsc && isOpen) {
      return closeOnEscape({onClose: closeAction})
    }
  }, [closeAction, closeOnEsc, isOpen])

  return (
    <>
      {prerenderContent && !isOpen && (
        <div aria-hidden="true" style={{display: 'none'}}>
          {children}
        </div>
      )}
      <Portal>
        <AnimatePresence>
          {isOpen && (
            <Wrapper
              className={className}
              variants={transitions.overlayFade}
              initial="exited"
              animate="entered"
              exit="exited"
              onClick={closeOnOutsideClick ? closeAction : () => null}
              style={style}
            >
              <div>
                <Content
                  maxWidth={width}
                  height={popUpHeight()}
                  data-id={dataId}
                  variants={transitions[ANIMATION_TYPES.SLIDE_DOWN]}
                  initial="exited"
                  animate="entered"
                  exit="exited"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  custom={TRANSITION_DURATION / 1000}
                >
                  <HeaderWrapper data-id="NavBar" header={centerContent}>
                    <NavBar
                      {...navBarRest}
                      isCompact
                      variant={variant}
                      floating={floating}
                      centerAligned={centerAligned}
                      withDivider={Boolean(withDivider)}
                      centerContent={
                        centerContent && (
                          <NavBarTitle>{centerContent}</NavBarTitle>
                        )
                      }
                      endContent={headerEndContent}
                    />
                  </HeaderWrapper>

                  <BodyWrapper
                    ref={popUpRef}
                    hasHeader={hasHeaderContent}
                    noPaddings={noPaddings}
                    footer={Boolean(footer)}
                  >
                    <Body>{children}</Body>
                  </BodyWrapper>

                  {footer && <BottomBar sticky>{footer}</BottomBar>}
                </Content>
              </div>
            </Wrapper>
          )}
        </AnimatePresence>
      </Portal>
    </>
  )
}
